import React from "react";
import { useEffect } from "react";
import { toast } from "sonner";
import { COMPANY } from "../../config/company";

/**
 * Hook for "Call Waiter / Bill" demo actions — shows a toast instead of real action.
 */
export function useDemoActions(restaurantName, lang = "de") {
  const labels = {
    de: { waiter: `🔔 Kellner gerufen! In der echten Version sieht ${restaurantName} es sofort.`, bill: `💶 Rechnung angefordert! Live im Dashboard.`, note: "Das ist eine Demo." },
    en: { waiter: `🔔 Waiter called! In the live version ${restaurantName} sees it instantly.`, bill: `💶 Bill requested! Live in dashboard.`, note: "This is a demo." },
    tr: { waiter: `🔔 Garson çağrıldı! Gerçek sürümde ${restaurantName} anında görür.`, bill: `💶 Hesap istendi! Canlı dashboard'da.`, note: "Bu bir demo." },
  };
  const l = labels[lang] || labels.de;
  return {
    callWaiter: () => toast.success(l.waiter, { description: l.note, duration: 4500 }),
    requestBill: () => toast.success(l.bill, { description: l.note, duration: 4500 }),
  };
}

/**
 * Set <title>, meta description, and inject Schema.org Restaurant JSON-LD.
 * Returns nothing — runs as side effect.
 */
export function useDemoSEO({ title, description, schema, lang }) {
  useEffect(() => {
    document.title = title;
    // Meta description
    let m = document.querySelector('meta[name="description"]');
    if (!m) {
      m = document.createElement("meta");
      m.setAttribute("name", "description");
      document.head.appendChild(m);
    }
    m.setAttribute("content", description);
    // <html lang>
    document.documentElement.setAttribute("lang", lang || "de");
    // JSON-LD
    const SCRIPT_ID = "demo-jsonld";
    let s = document.getElementById(SCRIPT_ID);
    if (!s) {
      s = document.createElement("script");
      s.id = SCRIPT_ID;
      s.type = "application/ld+json";
      document.head.appendChild(s);
    }
    s.textContent = JSON.stringify(schema);
    return () => {
      const el = document.getElementById(SCRIPT_ID);
      if (el) el.remove();
    };
  }, [title, description, schema, lang]);
}

/**
 * Mini language switcher used inside demo pages.
 */
export function DemoLangSwitch({ lang, setLang, tone = "light" }) {
  const langs = [
    { code: "de", flag: "🇩🇪" },
    { code: "en", flag: "🇬🇧" },
    { code: "tr", flag: "🇹🇷" },
  ];
  return (
    <div className={`inline-flex p-1 rounded-full ${tone === "dark" ? "bg-white/5 border border-white/10" : "bg-black/5 border border-black/10"}`} data-testid="demo-lang-switch">
      {langs.map((l) => (
        <button
          key={l.code}
          onClick={() => setLang(l.code)}
          className={`px-3 py-1 rounded-full text-xs font-bold transition-all ${
            lang === l.code
              ? tone === "dark"
                ? "bg-white text-black"
                : "bg-black text-white"
              : tone === "dark"
              ? "text-white/60 hover:text-white"
              : "text-black/60 hover:text-black"
          }`}
          data-testid={`demo-lang-${l.code}`}
        >
          {l.flag} {l.code.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

/**
 * "Powered by qrmenu-pro.com" footer — used by all 3 demos with consistent CTA.
 */
export function DemoFooterCTA({ lang = "de", tone = "light" }) {
  const t = {
    de: { title: "Möchtest du auch ein solches digitales Menü?", sub: "Erstelle dein eigenes QR-Menü in 5 Minuten — 15 Tage kostenlos testen.", cta: "Jetzt kostenlos starten", others: "Weitere Demos ansehen" },
    en: { title: "Want a digital menu like this?", sub: "Build your own QR menu in 5 minutes — 15-day free trial.", cta: "Start free now", others: "View other demos" },
    tr: { title: "Sen de böyle bir dijital menü ister misin?", sub: "Kendi QR menünü 5 dakikada oluştur — 15 gün ücretsiz dene.", cta: "Hemen ücretsiz başla", others: "Diğer demoları gör" },
  }[lang] || t.de;
  return (
    <section
      className={`relative py-14 sm:py-20 ${
        tone === "dark" ? "bg-zinc-950 text-white border-t border-white/10" : "bg-zinc-50 text-zinc-900 border-t border-zinc-200"
      }`}
      data-testid="demo-footer-cta"
    >
      <div className="container mx-auto px-6 max-w-3xl text-center">
        <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] uppercase tracking-widest font-bold ${tone === "dark" ? "bg-white/5 text-white/70 border border-white/10" : "bg-black/5 text-black/70 border border-black/10"}`}>
          <span>🚀</span> Powered by qrmenu-pro.com
        </div>
        <h2 className="mt-5 text-2xl sm:text-4xl font-bold tracking-tight">{t.title}</h2>
        <p className={`mt-3 text-sm sm:text-base ${tone === "dark" ? "text-white/60" : "text-zinc-600"}`}>{t.sub}</p>
        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <a
            href="/register"
            className={`inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold transition-all hover:scale-105 ${tone === "dark" ? "bg-white text-black hover:bg-zinc-200" : "bg-zinc-900 text-white hover:bg-zinc-800"}`}
            data-testid="demo-cta-register"
          >
            {t.cta} →
          </a>
          <a
            href="/#demos"
            className={`inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold transition-all ${tone === "dark" ? "border border-white/20 text-white hover:bg-white/5" : "border border-zinc-300 text-zinc-900 hover:bg-white"}`}
            data-testid="demo-cta-others"
          >
            {t.others}
          </a>
        </div>
        <div className="mt-6 flex flex-wrap gap-4 justify-center text-xs">
          <a href="/demo-turkish-restaurant" className={tone === "dark" ? "text-white/50 hover:text-white" : "text-zinc-500 hover:text-zinc-900"}>Turkish Restaurant</a>
          <span className={tone === "dark" ? "text-white/30" : "text-zinc-300"}>·</span>
          <a href="/demo-cafe" className={tone === "dark" ? "text-white/50 hover:text-white" : "text-zinc-500 hover:text-zinc-900"}>Minimal Café</a>
          <span className={tone === "dark" ? "text-white/30" : "text-zinc-300"}>·</span>
          <a href="/demo-steakhouse" className={tone === "dark" ? "text-white/50 hover:text-white" : "text-zinc-500 hover:text-zinc-900"}>Premium Steakhouse</a>
        </div>
      </div>
    </section>
  );
}

/**
 * Sticky action bar (Call Waiter + Bill) used by all 3 demos.
 */
export function StickyActions({ onWaiter, onBill, lang = "de", theme }) {
  const labels = {
    de: { waiter: "Kellner rufen", bill: "Rechnung" },
    en: { waiter: "Call waiter", bill: "Bill" },
    tr: { waiter: "Garson çağır", bill: "Hesap" },
  }[lang] || { waiter: "Kellner rufen", bill: "Rechnung" };
  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex gap-2 pointer-events-none" data-testid="demo-sticky-actions">
      <button
        onClick={onWaiter}
        className={`pointer-events-auto inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-bold shadow-2xl backdrop-blur-md transition-all hover:scale-105 active:scale-95 ${theme?.waiterCls || "bg-purple-600 text-white"}`}
        data-testid="demo-call-waiter"
      >
        <span>🔔</span> {labels.waiter}
      </button>
      <button
        onClick={onBill}
        className={`pointer-events-auto inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-bold shadow-2xl backdrop-blur-md transition-all hover:scale-105 active:scale-95 ${theme?.billCls || "bg-emerald-600 text-white"}`}
        data-testid="demo-request-bill"
      >
        <span>💶</span> {labels.bill}
      </button>
    </div>
  );
}

/**
 * Helper to build Schema.org Restaurant JSON-LD object.
 */
export function buildRestaurantSchema({ url, name, image, priceRange, cuisine, address, telephone, sameAs, menu }) {
  return {
    "@context": "https://schema.org",
    "@type": "Restaurant",
    "@id": url,
    url,
    name,
    image,
    priceRange,
    servesCuisine: cuisine,
    telephone: telephone || COMPANY.phone || undefined,
    sameAs: sameAs || [COMPANY.instagramUrl],
    address: address || {
      "@type": "PostalAddress",
      addressCountry: "DE",
    },
    hasMenu: menu ? { "@type": "Menu", "name": "Speisekarte", "hasMenuSection": menu } : undefined,
    acceptsReservations: true,
  };
}

/**
 * Email-based reservation button (mailto).
 */
export function ReserveButton({ lang = "de", message, className, dataTestid = "demo-reserve-email" }) {
  const labels = { de: "Per E-Mail reservieren", en: "Reserve via email", tr: "E-posta ile rezervasyon" }[lang] || "Reserve";
  const subjects = { de: "Reservierungsanfrage", en: "Reservation request", tr: "Rezervasyon talebi" };
  const fallback = { de: "Hallo, ich möchte einen Tisch reservieren.", en: "Hi, I'd like to reserve a table.", tr: "Merhaba, masa rezervasyonu yapmak istiyorum." }[lang];
  const subj = subjects[lang] || subjects.de;
  const url = `mailto:${COMPANY.publicEmail}?subject=${encodeURIComponent(subj)}&body=${encodeURIComponent(message || fallback)}`;
  return (
    <a
      href={url}
      className={className || "inline-flex items-center gap-2 px-5 py-3 rounded-full bg-purple-600 hover:bg-purple-500 text-white font-semibold text-sm transition-all hover:scale-105"}
      data-testid={dataTestid}
    >
      <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden>
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2zm8 7L4 6h16l-8 5zm0 2.5L4 8.5V18h16V8.5l-8 5z"/>
      </svg>
      {labels}
    </a>
  );
}
